import React, { useRef, useEffect, useCallback } from 'react'
import { FormSource } from '@formio/react'
import hasComponentTypes from 'src/pages/FormTemplatePage/util/hasComponentTypes'
import toast from 'react-hot-toast'

interface FormBuilderOptions {
  builder?: Record<string, unknown>
  noDefaultSubmitButton?: boolean
  [key: string]: unknown
  prohibitedElements?: string[]
}

interface IsolatedFormBuilderProps {
  currentForm?: FormSource
  options?: FormBuilderOptions
  onChange?: (form: FormSource) => void
  style?: React.CSSProperties
  className?: string
}

const IsolatedFormBuilder: React.FC<IsolatedFormBuilderProps> = ({
  currentForm,
  options = {},
  onChange,
  style,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return // Security check
      }

      const { type, form } = event.data

      switch (type) {
        case 'FORM_CHANGED':
          if (
            options.prohibitedElements?.length &&
            hasComponentTypes(form, options.prohibitedElements)
          ) {
            toast.error('Public forms cannot contain digital signature fields')
            iframeRef.current?.contentWindow.postMessage(
              { type: 'FORM_UPDATED', data: { form: currentForm } },
              window.location.origin
            )
            return
          }
          onChange?.(form)
          break
      }
    },
    [currentForm, options, onChange]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  const initializeFormBuilder = () => {
    iframeRef.current?.contentWindow.postMessage(
      { type: 'INIT_BUILDER', data: { initialForm: currentForm, options } },
      window.location.origin
    )
  }

  useEffect(() => {
    iframeRef.current?.addEventListener('load', initializeFormBuilder)
    return () =>
      iframeRef.current?.removeEventListener('load', initializeFormBuilder)
  }, [])

  return (
    <iframe
      ref={iframeRef}
      src="/form-builder.html"
      className={className}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        ...style,
      }}
      title="Form Builder"
    />
  )
}

export default IsolatedFormBuilder
